import { useCallback } from 'react';
import {
  QueryObserverSuccessResult,
  QueryObserverRefetchErrorResult,
  QueryObserverLoadingErrorResult,
} from '@tanstack/react-query';
import type {
  EnvironmentKey,
  DomainEntity,
  GetEndpointResponse,
} from '@web-config-app/core';
import {
  UseEntityGetOptions,
  useEntityGet,
} from '../use-entity-get/use-entity-get';

interface UseFindEntityInstanceByIdArgs {
  domainEntity: DomainEntity;
  instanceId: string;
  environment: EnvironmentKey;
  options?: UseEntityGetOptions;
}

interface FindEntityByIdArgs {
  onSuccess: (
    res: QueryObserverSuccessResult<GetEndpointResponse, Error>,
  ) => void;
  onError?: (
    res:
      | QueryObserverRefetchErrorResult<GetEndpointResponse, Error>
      | QueryObserverLoadingErrorResult<GetEndpointResponse, Error>,
  ) => void;
}

export function useFindEntityInstanceById({
  domainEntity,
  instanceId,
  options,
  environment,
}: UseFindEntityInstanceByIdArgs) {
  // Every entity now carries its own endpoints — CACT-BE entities get them pre-merged at
  // tenant-config build time; entities with `isUsingConfigAppBackend: false` ship them inline
  // — so the bare entity can be handed straight to `useEntityGet` without wrapping.
  const useQueryResult = useEntityGet({
    entity: domainEntity,
    instanceId,
    options,
    environment,
  });
  const { refetch } = useQueryResult;

  const findEntityById = useCallback(
    async ({ onSuccess, onError }: FindEntityByIdArgs) => {
      if (!instanceId) return;

      const res = await refetch();
      const getSuccess = res.status === 'success' && res.data.data;

      if (getSuccess) {
        onSuccess(res);
      } else if (res.status === 'error') {
        onError?.(res);
      }
    },
    [instanceId, refetch],
  );

  return {
    ...useQueryResult,
    findEntityById,
  };
}
